// src/pages/LoginPage.tsx
import config from '../config/config'; // Import der Konfigurationsdatei
import ModuleAppBar from '../modules/module_appbar';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Input, Checkbox } from '@progress/kendo-react-inputs';
import styled from 'styled-components';
import { Button } from '@progress/kendo-react-buttons';
import { Card, CardBody, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { useAuth } from '../contexts/AuthContext'; // Import the useAuth hook

// Loader-Import
import { Loader } from '@progress/kendo-react-indicators';

interface Credentials {
  EBTBusername: string;
  EBTBpassword: string;
}

const StyledButton = styled(Button)`
    font-weight: bold;
    font-size: 20px;
`;

const UNSPLASH_ACCESS_KEY = 'gmtpktF-Tehpfe6D-tj18sxzX-BABuramv0ws4Qcec4';
const UNSPLASH_API_URL = `https://api.unsplash.com/photos/random?query=wallpapers&client_id=${UNSPLASH_ACCESS_KEY}`;

// Wert "LoadLocalRandomPics" hart im Code gesetzt.
const LoadLocalRandomPics = true; // true => /random_image, false => Unsplash

const LoginPage: React.FC = () => {
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [credentials, setCredentials] = useState<Credentials>({ EBTBusername: '', EBTBpassword: '' });
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  const navigate = useNavigate();
  const { setUserId } = useAuth(); // Use the useAuth hook to get setUserId

  // Ladezustand für den Loader
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      try {
        if (LoadLocalRandomPics) {
          // Lokale Bilder
          setBackgroundImage(`${config.host}/random_image`);
        } else {
          // Unsplash
          const response = await axios.get(UNSPLASH_API_URL);
          setBackgroundImage(response.data.urls.full);
        }
      } catch (error) {
        console.error('Error fetching background image:', error);
      }
    };
    fetchBackgroundImage();
  }, []);

  const handleChange = (event: any): void => {
    const { name, value } = event.target;
    setCredentials((prevCredentials) => ({ ...prevCredentials, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true); // Ladezustand aktivieren

    try {
      if (!credentials.EBTBusername || !credentials.EBTBpassword) {
        setAlertMessage('Bitte geben Sie Benutzernamen und Kennwort ein.');
        return;
      }

      const response = await axios.post(`${config.host}/login`, {
        username: credentials.EBTBusername,
        password: credentials.EBTBpassword,
      });

      if (response.data.success) {
        const userId = response.data.userId;
        setUserId(userId); // Update userId in context
        navigate(response.data.redirect || '/construction-diary');
      } else {
        setAlertMessage('Login fehlgeschlagen!');
      }
    } catch (error) {
      setAlertMessage('Unknown username or wrong password!');
    } finally {
      // Ladezustand wieder deaktivieren
      setLoading(false);
    }
  };

  return (
    <div
      className="k-flex k-justify-center k-items-center k-h-screen"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        height: '100vh',
      }}
    >
      <ModuleAppBar />

      {/* Die Card bekommt teiltransparentes Weiß + Blur-Effekt im Hintergrund */}
      <Card
        className="k-shadow-lg" // k-bg-white entfernt
        style={{
          width: '400px',
          marginTop: '200px',
          marginLeft: 'auto',
          marginRight: 'auto',
          backgroundColor: 'rgba(255, 255, 255, 0.75)', // 80 % weiß
          backdropFilter: 'blur(6px)',                 // Hintergrund direkt hinter der Card unscharf
        }}
      >
        <CardHeader>
          <CardTitle><b>Anmeldung</b></CardTitle>
        </CardHeader>
        <CardBody>
          {alertMessage && (
            <NotificationGroup>
              <Notification type={{ style: 'error', icon: true }}>
                {alertMessage}
              </Notification>
            </NotificationGroup>
          )}
          <form onSubmit={handleSubmit} className="k-mt-4">
            <div className="k-mb-3">
              <label className="k-form-label">Benutzername</label>
              <Input
                name="EBTBusername"
                value={credentials.EBTBusername}
                onChange={handleChange}
                required
              />
            </div>
            <div className="k-mb-3">
              <label className="k-form-label">Passwort</label>
              <Input
                name="EBTBpassword"
                type="password"
                value={credentials.EBTBpassword}
                onChange={handleChange}
                required
              />
            </div>
            <div className="k-mb-3">
              <Checkbox 
                label="Angemeldet bleiben" 
                style={{ marginTop: '15px', marginBottom: '15px' }}
              />
            </div>
            <StyledButton
              themeColor="primary"
              style={{ width: '100%', marginTop: '10px' }}
              type="submit"
            >
              Login
            </StyledButton>
          </form>

        </CardBody>
      </Card>

      {/* Overlay + Loader, wird nur angezeigt, wenn loading === true */}
      {loading && (
        <div
          style={{
            position: 'fixed',
            zIndex: 9999,
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Loader size="large" type="infinite-spinner" />
            <p><b>Anmeldung läuft...</b></p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
